import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  CriticalIcon,
  EmptyState,
  ExternalLinkIcon,
  Flex,
  Link,
  Notice,
  Skeleton,
} from '@buffer-mono/popcorn'
import {
  Client,
  BufferTrackerReact as BufferTracker,
} from '@buffer-mono/tracking-plan'

import type { BillingInterval, ObPlanId } from '../../gql/graphql'
import { MODALS } from '../../common/types'
import { AssistedBillingNotice } from '../AssistedBillingNotice'
import { PlanOverrideConfirmationDialog } from '../PlanOverrideConfirmationDialog/PlanOverrideConfirmationDialog'
import { SubscriptionUpdateModal } from '../Modal/modals/SubscriptionUpdate'
import type { SubscriptionUpdate } from '../Modal/modals/SubscriptionUpdate'
import { useModalManager } from '../ModalManager/hooks/useModalManager'

import BillingToggle from './components/BillingToggle/BillingToggle'
import ChannelSelector from './components/ChannelSelector/ChannelSelector'
import CurrentPlanInfo from './components/CurrentPlanInfo/CurrentPlanInfo'
import PlanCard from './components/PlanCard/PlanCard'
import { usePlanRecommendation } from './hooks/usePlanRecommendation'
import { usePlanSelector } from './hooks/usePlanSelector'
import { usePlanSelectorTracking } from './hooks/usePlanSelectorTracking'
import type { UserData } from './hooks/usePlanSelectorTracking'
import { usePlanOverrideDialog } from './hooks/usePlanOverrideDialog'
import {
  detectPlanSelectorCohort,
  PlanSelectorCohort,
} from './utils/cohortDetection'
import { openTieredPricingContactForm } from './utils/contactFormHelpers'

import {
  isPlanUpgrade as checkIsPlanUpgrade,
  isPlanDowngrade as checkIsPlanDowngrade,
  isPlanUpdate as checkIsPlanUpdate,
} from './utils/planComparison'

import useCheckoutSession from './hooks/useCheckoutSession'
import { useUpdateBillingSubscriptionPlan } from './hooks/useUpdateBillingSubscriptionPlan'
import { OBPlanIdValues, UpdatePlanPhase } from './types'
import { getUpdateSubscriptionTypeConfig } from './utils/getUpdateSubscriptionTypeConfig'
import { formatPrice } from './utils/pricingCalculations'

import styles from './PlanSelector.module.css'
import { shouldShowPlanOverrideDialog } from './utils/planOverrideHelpers'
import { useStripePaymentConfirmation } from '../../hooks/useStripePaymentConfirmation'
import { useScaRetryState } from '../../hooks/useScaRetryState'
import { SubscriptionUpdateStatus } from '../../gql/graphql'

export interface PlanSelectorProps {
  onClose?: () => void
  onLoadingChange?: (loading: boolean) => void
  // Optional tracking props for when PlanSelector is embedded in a page
  cta?: string
  ctaButton?: string
  upgradePathName?: string
  // Optional user data for tracking when PlanSelector is used outside app-shell context
  user?: UserData
}

/**
 * A comprehensive plan selection interface that allows users to browse and select
 * billing plans with customizable channel counts and billing cycles.
 *
 * Features:
 * - Current plan information display
 * - Channel count selector
 * - Monthly/yearly billing toggle
 * - Plan comparison cards
 * - Pricing calculations and savings display
 *
 * @param onPlanSelect - Callback function when a plan is selected
 */
export const PlanSelector = ({
  cta,
  ctaButton,
  upgradePathName,
  user: userProp,
  onClose,
  onLoadingChange,
}: PlanSelectorProps): JSX.Element => {
  const {
    data,
    loading,
    error,
    selectChannels,
    selectBillingCycle,
    selection,
    currentUser,
    isPlanCurrent,
    isPlanUpgrade,
    isPlanDowngrade,
    isPlanUpdate,
    isPayingViaMobile,
    refetch,
  } = usePlanSelector()

  const [invoiceError, setInvoiceError] = useState<boolean>(false)
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false)
  const [subscriptionUpdateData, setSubscriptionUpdateData] =
    useState<SubscriptionUpdate | null>(null)
  const [updatingPlanId, setUpdatingPlanId] = useState<ObPlanId | null>(null)
  const [updatePlanPhase, setUpdatePlanPhase] = useState<UpdatePlanPhase>(
    UpdatePlanPhase.Idle,
  )

  // Payment retry state using centralized hook (VLG-394)
  const {
    retryState,
    saveRetryData,
    clearRetryData,
    hasParametersChanged,
    canRetry,
  } = useScaRetryState<ObPlanId, BillingInterval>()

  // Check if we're in the app-shell modal context
  const modalManager = useModalManager()
  const isInModalContext = Boolean(modalManager.modalKey)

  // Detect which cohort the user belongs to for conditional UX
  // Only compute when both subscription and available plans data are loaded
  const cohort =
    data?.subscription && data?.availablePlans
      ? detectPlanSelectorCohort(data.subscription, data.availablePlans)
      : null

  // Initialize tracking with the detected cohort
  // planSelectorVersion is always explicitly set based on the detected cohort
  // We use nullish coalescing only for hook initialization (React rules of hooks),
  // but tracking won't fire until cohort is resolved (see useEffect guard below)
  const planSelectorVersion = cohort ?? PlanSelectorCohort.Tiered
  const {
    trackChannelQuantityChange,
    trackBillingCycleChange,
    trackPlanSelection,
    trackViewed,
  } = usePlanSelectorTracking({
    currentUser,
    selection,
    cta,
    ctaButton,
    upgradePathName,
    user: userProp,
    planSelectorVersion,
  })

  const { recommendedPlanId } = usePlanRecommendation({
    availablePlans: data?.availablePlans || [],
    currentPlanId: currentUser.currentPlan.planId,
    channelCount: selection.channelCount,
    hasEverBeenPayingCustomer: currentUser.hasEverBeenPayingCustomer,
  })
  const scheduledUpdate = data?.subscription?.scheduledUpdate
  const hasScheduledDowngrade = Boolean(
    scheduledUpdate &&
    checkIsPlanDowngrade(scheduledUpdate.newPlan, {
      currentPlanId: currentUser.currentPlan.planId,
      currentChannelCount: currentUser.currentPlan.channelCount,
      selectedChannelCount: scheduledUpdate.newQuantity,
    }),
  )

  const isOnFreePlan = currentUser.currentPlan.planId === OBPlanIdValues.free

  // A negative balance is credit that Stripe applies to the next invoice. The
  // credit cannot reduce an Apple or Google bill, so mobile-billed users do
  // not see it.
  const accountCredit =
    data && !isPayingViaMobile && data.balance.amount < 0
      ? formatPrice(-data.balance.amount, data.balance.currency.toUpperCase())
      : null

  // Track when PlanSelector is mounted and cohort is resolved
  // Use ref to ensure we only track once
  const hasTrackedViewedRef = useRef(false)
  useEffect(() => {
    if (!cohort || hasTrackedViewedRef.current) return
    trackViewed()
    hasTrackedViewedRef.current = true
  }, [cohort, trackViewed])

  const { checkoutSession } = useCheckoutSession()
  const { updatePlan, loading: updatePlanLoading } =
    useUpdateBillingSubscriptionPlan()

  // Plan override dialog hook
  const { showOverrideConfirmation, dialogProps } =
    usePlanOverrideDialog(updatePlanLoading)

  // SCA payment confirmation hook
  const {
    confirmPayment,
    isConfirming,
    error: paymentError,
    isFeatureEnabled: isScaEnabled,
  } = useStripePaymentConfirmation()

  // Notify parent component when loading state changes
  useEffect(() => {
    const isLoading = updatePlanLoading || isConfirming
    onLoadingChange?.(isLoading)

    return () => {
      onLoadingChange?.(false)
    }
  }, [updatePlanLoading, isConfirming, onLoadingChange])

  const handleDirectToStripeCheckout = useCallback(
    async ({
      planId,
      billingCycle,
      channelCount,
    }: {
      planId: ObPlanId
      billingCycle: BillingInterval
      channelCount: number
    }): Promise<void> => {
      await checkoutSession({
        planName: planId,
        interval: billingCycle,
        quantity: channelCount,
        cta,
        upgradePathName,
        initialQuantity: currentUser.currentPlan.channelCount,
        initialSelectedPlan: currentUser.currentPlan.planId,
        initialInterval: currentUser.currentPlan.billingCycle,
      })
    },
    [checkoutSession, currentUser.currentPlan, cta, upgradePathName],
  )

  const handleUpdateSubscription = useCallback(
    async (
      planId: ObPlanId,
      billingCycle: BillingInterval,
      channelCount: number,
    ): Promise<void> => {
      const currentParams = {
        planId,
        interval: billingCycle,
        quantity: channelCount,
      }

      // SCENARIO A: Pure retry - reuse client secret (VLG-394)
      if (
        isScaEnabled &&
        canRetry() &&
        !hasParametersChanged(currentParams) &&
        retryState.session
      ) {
        setUpdatingPlanId(planId)
        setUpdatePlanPhase(UpdatePlanPhase.Authorizing)

        let confirmResult
        try {
          confirmResult = await confirmPayment(
            retryState.session.clientSecret,
            retryState.session.paymentMethodId ?? undefined,
          )
        } catch {
          setUpdatingPlanId(null)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          throw new Error('Payment authorization failed. Please try again.')
        }

        if (confirmResult.success) {
          // Success - clear retry state
          clearRetryData()

          setUpdatePlanPhase(UpdatePlanPhase.Confirming)
          await refetch()

          // Get the update type
          const updateType = getUpdateSubscriptionTypeConfig({
            planId,
            channelCount,
            currentChannelCount: currentUser.currentPlan.channelCount,
            isPlanDowngrade,
            isPlanUpgrade,
          })

          // Build success payload
          const subscriptionUpdate: SubscriptionUpdate = {
            updateType,
            planId,
            currentPlanId: currentUser.currentPlan.planId,
            channelCount,
            scheduledUpdate: null, // Immediate upgrade after SCA
          }

          // Clear updating state
          setUpdatingPlanId(null)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)

          // Show celebration modal
          if (isInModalContext) {
            modalManager.dismissModal()
            modalManager.openModal({
              key: MODALS.subscriptionUpdateModern,
              data: {
                subscriptionUpdate,
              },
            })
          } else {
            setSubscriptionUpdateData(subscriptionUpdate)
            setShowSuccessModal(true)
          }
          return
        } else {
          // Still failed - keep retry mode active (state unchanged)
          setUpdatingPlanId(null)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          throw new Error('Payment authorization failed. Please try again.')
        }
      }

      // SCENARIO B: New attempt or parameters changed (VLG-394)
      // Clear any existing retry state
      clearRetryData()

      // Set updating state
      setUpdatingPlanId(planId)
      setUpdatePlanPhase(UpdatePlanPhase.Updating)

      // Get the update type
      const updateType = getUpdateSubscriptionTypeConfig({
        planId,
        channelCount,
        currentChannelCount: currentUser.currentPlan.channelCount,
        isPlanDowngrade,
        isPlanUpgrade,
      })

      // Build and execute the update with attribution always included
      const updatePayload = {
        plan: planId,
        interval: billingCycle,
        quantity: channelCount,
        attribution: {
          cta,
          upgradePathName,
        },
      }

      const response = await updatePlan(updatePayload)

      // Check if payment requires additional authentication (SCA)
      if (
        isScaEnabled &&
        response?.status === SubscriptionUpdateStatus.RequiresAction &&
        response?.paymentAction?.clientSecret
      ) {
        // Save retry session for potential retry (VLG-394)
        saveRetryData({
          clientSecret: response.paymentAction.clientSecret,
          paymentMethodId: response.paymentAction.paymentMethodId ?? null,
          planId,
          interval: billingCycle,
          quantity: channelCount,
        })

        // Set phase to authorizing before showing Stripe modal
        setUpdatePlanPhase(UpdatePlanPhase.Authorizing)

        // Trigger Stripe 3D Secure authentication
        let confirmResult
        try {
          confirmResult = await confirmPayment(
            response.paymentAction.clientSecret,
            response.paymentAction.paymentMethodId ?? undefined,
          )
        } catch {
          // Handle unexpected errors from confirmPayment (network failures, etc.)
          setUpdatingPlanId(null)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          // Retry mode already enabled by saveRetryData above
          throw new Error('Payment authorization failed. Please try again.')
        }

        if (confirmResult.success) {
          // Payment confirmation succeeded - Stripe automatically updates subscription
          // Clear retry state (VLG-394)
          clearRetryData()

          // Set phase to confirming to show feedback while we refetch
          setUpdatePlanPhase(UpdatePlanPhase.Confirming)

          // Refetch to get updated subscription data
          await refetch()

          // Build success payload (same as normal flow)
          const subscriptionUpdate: SubscriptionUpdate = {
            updateType,
            planId,
            currentPlanId: currentUser.currentPlan.planId,
            channelCount,
            scheduledUpdate: null, // Immediate upgrade after SCA
          }

          // Clear updating state
          setUpdatingPlanId(null)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)

          // Show celebration modal (match non-SCA path)
          if (isInModalContext) {
            modalManager.dismissModal()
            modalManager.openModal({
              key: MODALS.subscriptionUpdateModern,
              data: {
                subscriptionUpdate,
              },
            })
          } else {
            setSubscriptionUpdateData(subscriptionUpdate)
            setShowSuccessModal(true)
          }
          return
        } else {
          // Payment confirmation failed or was canceled
          // Retry mode already enabled by saveRetryData above
          setUpdatingPlanId(null)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          throw new Error('Payment authorization failed. Please try again.')
        }
      }

      // Normal flow - no SCA required or SCA not enabled
      // Refetch to get updated subscription data before showing modal
      await refetch()

      // Clear updating state
      setUpdatingPlanId(null)
      setUpdatePlanPhase(UpdatePlanPhase.Idle)

      const subscriptionUpdate: SubscriptionUpdate = {
        updateType,
        planId,
        currentPlanId: currentUser.currentPlan.planId,
        channelCount,
        scheduledUpdate: response?.scheduledUpdate || null,
      }

      if (isInModalContext) {
        // We're in the app-shell modal context - close current modal and open new one
        modalManager.dismissModal()
        modalManager.openModal({
          key: MODALS.subscriptionUpdateModern,
          data: {
            subscriptionUpdate,
          },
        })
      } else {
        // We're in standalone mode - render the modal directly
        setSubscriptionUpdateData(subscriptionUpdate)
        setShowSuccessModal(true)
      }
    },
    [
      canRetry,
      hasParametersChanged,
      retryState.session,
      clearRetryData,
      saveRetryData,
      updatePlan,
      isPlanDowngrade,
      isPlanUpgrade,
      currentUser.currentPlan.channelCount,
      cta,
      upgradePathName,
      currentUser.currentPlan.planId,
      isInModalContext,
      modalManager,
      isScaEnabled,
      confirmPayment,
      refetch,
    ],
  )

  const handleInvoiceError = useCallback(() => {
    setInvoiceError(true)
  }, [])

  const handleOnPlanSelect = useCallback(
    async ({
      planId,
      billingCycle,
      channelCount,
    }: {
      planId: ObPlanId
      billingCycle: BillingInterval
      channelCount: number
    }): Promise<void> => {
      trackPlanSelection(planId, channelCount)

      const isSelectingFreePlan = planId === OBPlanIdValues.free
      const isDowngradingToFree = isSelectingFreePlan && !isOnFreePlan

      // If downgrading to free, show churn survey modal first
      if (isDowngradingToFree && isInModalContext) {
        modalManager.dismissModal()
        modalManager.openModal({
          key: MODALS.churnSurvey,
          data: {
            isOnTrial: Boolean(currentUser.trial),
          },
        })
        return
      }

      // Always use updateSubscription for free plans or when user has payment method
      if (
        isSelectingFreePlan ||
        (currentUser.hasPaymentMethod && !isOnFreePlan)
      ) {
        // Check if we need to show the override dialog
        if (
          data?.subscription &&
          shouldShowPlanOverrideDialog(data?.subscription)
        ) {
          const confirmed = await showOverrideConfirmation(data?.subscription)
          if (!confirmed) {
            return
          }
        }

        await handleUpdateSubscription(planId, billingCycle, channelCount)
      } else {
        // Only use checkout session for new paid subscriptions
        await handleDirectToStripeCheckout({
          planId,
          billingCycle,
          channelCount,
        })
      }
    },
    [
      currentUser.hasPaymentMethod,
      currentUser.trial,
      handleDirectToStripeCheckout,
      handleUpdateSubscription,
      trackPlanSelection,
      isOnFreePlan,
      showOverrideConfirmation,
      data?.subscription,
      isInModalContext,
      modalManager,
    ],
  )

  // Used to determine UI for Plan Cards
  // Takes into consideration if a user has a scheduled downgrade
  const effectivePlanId = useMemo(
    () =>
      hasScheduledDowngrade && scheduledUpdate
        ? scheduledUpdate.newPlan
        : currentUser.currentPlan.planId,
    [hasScheduledDowngrade, scheduledUpdate, currentUser.currentPlan.planId],
  )

  // Create comparison config for the effective plan state
  const effectiveComparisonConfig = useMemo(
    () => ({
      currentPlanId: effectivePlanId,
      currentChannelCount:
        hasScheduledDowngrade && scheduledUpdate
          ? scheduledUpdate.newQuantity
          : currentUser.currentPlan.channelCount,
      selectedChannelCount: selection.channelCount,
    }),
    [
      effectivePlanId,
      hasScheduledDowngrade,
      scheduledUpdate,
      currentUser.currentPlan.channelCount,
      selection.channelCount,
    ],
  )

  if (data?.requiresAssistedBilling) {
    return (
      <main className={styles.planSelector} aria-label="Plan selector">
        <AssistedBillingNotice />
      </main>
    )
  }

  if (error || invoiceError) {
    return (
      <main className={styles.planSelector} aria-label="Plan selector">
        <EmptyState variant="critical" style={{ height: '400px' }}>
          <EmptyState.Icon>
            <CriticalIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>
            {invoiceError
              ? 'Unable to load next payment amount'
              : 'Unable to load plans'}
          </EmptyState.Heading>
          <EmptyState.Description>
            Try refreshing your browser. If the issue persists,{' '}
            <Link
              as="button"
              onClick={(): void => {
                window.Brainfish?.Widgets?.open()
              }}
            >
              contact support
            </Link>
            .
          </EmptyState.Description>
        </EmptyState>
        <Flex justify="center">
          <Button
            variant="secondary"
            size="large"
            as="a"
            href="https://buffer.com/pricing#compare-plans"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.compareButton}
            onClick={() => {
              BufferTracker.cTAClicked({
                cta: 'billing-planSelector-compareFeatures',
                clientName: Client.PublishWeb,
                upgradePathName: 'billing-planSelector',
                product: 'publish',
              })
            }}
            aria-label="Compare all features"
          >
            <ExternalLinkIcon />
            Compare All Features
          </Button>
        </Flex>
      </main>
    )
  }

  return (
    <main className={styles.planSelector} aria-label="Plan selector">
      <Flex
        align="center"
        justify="between"
        gap="md"
        fullWidth
        className={styles.header}
      >
        <CurrentPlanInfo
          currentPlan={currentUser.currentPlan}
          isCanceledAtPeriodEnd={data?.subscription?.isCanceledAtPeriodEnd}
          periodEnd={data?.subscription?.periodEnd}
        />

        <Flex gap="md" align="center" className={styles.controls}>
          <ChannelSelector
            value={selection.channelCount}
            onChange={(count) => {
              selectChannels(count)
              trackChannelQuantityChange(count)
            }}
            planPricing={
              data?.availablePlans.find(
                // REVIEW: This should handle different plans as their tier pricing may not be the same
                // currently essentials and team have the same pricing tiers but may change in the future
                (plan) => plan.planId === OBPlanIdValues.essentials,
              )?.pricing
            }
          />

          <BillingToggle
            value={selection.billingCycle}
            onChange={(cycle) => {
              selectBillingCycle(cycle)
              trackBillingCycleChange(cycle, selection.channelCount)
            }}
          />
        </Flex>
      </Flex>

      <Flex gap="xl" direction="column" align="center" fullWidth>
        {accountCredit && (
          <Notice variant="info">
            You have {accountCredit} in credit. It applies automatically to your
            next payment.
          </Notice>
        )}

        {isPayingViaMobile && (
          <Notice variant="info">
            You will need to use the Buffer mobile app to make changes to your
            subscription.
          </Notice>
        )}

        {loading ? (
          <Flex
            justify="center"
            align="center"
            gap="md"
            className={styles.loadingContainer}
          >
            <Skeleton width="100%" height="100%" />
            <Skeleton width="100%" height="100%" />
            <Skeleton width="100%" height="100%" />
          </Flex>
        ) : (
          <div className={styles.planCards}>
            {data?.availablePlans.map((plan) => {
              const isEffectivelyCurrent = plan.planId === effectivePlanId
              const isEffectivelyUpgrade =
                !isEffectivelyCurrent &&
                checkIsPlanUpgrade(plan.planId, effectiveComparisonConfig)
              const isEffectivelyDowngrade =
                !isEffectivelyCurrent &&
                checkIsPlanDowngrade(plan.planId, effectiveComparisonConfig)
              // Check if scheduled plan needs update (e.g., channel count changed)
              const effectiveNeedsUpdate =
                isEffectivelyCurrent &&
                checkIsPlanUpdate(plan.planId, effectiveComparisonConfig)
              return (
                <PlanCard
                  key={plan.planId}
                  plan={plan}
                  channelCount={selection.channelCount}
                  billingCycle={selection.billingCycle}
                  onSelect={() =>
                    handleOnPlanSelect({
                      planId: plan.planId,
                      billingCycle: selection.billingCycle,
                      channelCount: selection.channelCount,
                    })
                  }
                  isCurrent={isPlanCurrent(plan.planId)}
                  isUpgrade={
                    !isPlanCurrent(plan.planId) && isEffectivelyUpgrade
                  }
                  isDowngrade={
                    !isPlanCurrent(plan.planId) && isEffectivelyDowngrade
                  }
                  needsUpdate={
                    isPlanUpdate(plan.planId) || effectiveNeedsUpdate
                  }
                  isRecommended={recommendedPlanId === plan.planId}
                  isPayingViaMobile={isPayingViaMobile}
                  onInvoiceError={handleInvoiceError}
                  isAnyPlanUpdating={updatePlanLoading || isConfirming}
                  updatingPlanId={updatingPlanId}
                  updatePlanPhase={updatePlanPhase}
                  isOnTrial={Boolean(currentUser.trial)}
                  showTieredPricingCta={
                    cohort === PlanSelectorCohort.NonTiered &&
                    plan.planId === OBPlanIdValues.team &&
                    !isPlanCurrent(plan.planId)
                  }
                  onTieredPricingCtaClick={() => {
                    BufferTracker.cTAClicked({
                      cta: 'billing-planSelector-tieredPricingContact',
                      clientName: Client.PublishWeb,
                      upgradePathName: 'billing-planSelector',
                      product: 'publish',
                    })
                    openTieredPricingContactForm(window.__userData?.name)
                  }}
                />
              )
            })}
          </div>
        )}

        <Button
          variant="secondary"
          size="large"
          as="a"
          href="https://buffer.com/pricing#compare-plans"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.compareButton}
          onClick={() => {
            BufferTracker.cTAClicked({
              cta: 'billing-planSelector-compareFeatures',
              clientName: Client.PublishWeb,
              upgradePathName: 'billing-planSelector',
              product: 'publish',
            })
          }}
          aria-label="Compare all features"
        >
          <ExternalLinkIcon />
          Compare All Features
        </Button>
      </Flex>

      {/* Plan Override Confirmation Dialog */}
      {dialogProps.subscription && (
        <PlanOverrideConfirmationDialog
          subscription={dialogProps.subscription}
          onConfirm={dialogProps.onConfirm}
          loading={dialogProps.loading}
          open={dialogProps.open}
          onOpenChange={dialogProps.onOpenChange}
        />
      )}

      {/* Success Modal - Direct Rendering (only when not in modal context) */}
      {!isInModalContext && subscriptionUpdateData && (
        <SubscriptionUpdateModal
          open={showSuccessModal}
          onOpenChange={(open) => {
            setShowSuccessModal(open)
            if (!open) {
              setSubscriptionUpdateData(null)
            }
            // When modal closes in standalone mode, call onClose if provided
            if (!open && onClose) {
              onClose()
            }
          }}
          subscriptionUpdate={subscriptionUpdateData}
        />
      )}
    </main>
  )
}
